import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { faArrowLeft, faClipboard, faCheck, faLink, faTag, faInfoCircle, faTriangleExclamation } from '@/fontawesome';
import type { SubscriptionPriceProduct, SubscriptionType, User } from '@/models';
import { PROMOTION_CONFIG, LANDING_PROMOTION_CONFIG } from '@/consts/promotion';
import UserSelect from '@/components/admin/common/UserSelect';
import { useSubscriptionProducts } from '@/contexts/SubscriptionProductsContext';
import { hasExternalSuccessPage, hasStripeCheckout } from '@/services/subscription-products';

// Base URL of the public börsencampus site, configured per environment.
// Falls back to the current origin (e.g. for localrun where the var is empty).
function getDefaultBaseUrl(): string {
  const configured = (import.meta.env.VITE_BOERSENCAMPUS_URL ?? '').replace(/\/$/, '');
  if (configured !== '') {
    return configured;
  }
  return window.location.origin;
}

// Known coupon codes maintained in the frontend promotion config, offered as quick-select.
function getKnownCoupons(): string[] {
  const codes: string[] = [];
  if (PROMOTION_CONFIG.couponCode.trim() !== '') {
    codes.push(PROMOTION_CONFIG.couponCode.trim());
  }
  if (LANDING_PROMOTION_CONFIG !== null && LANDING_PROMOTION_CONFIG.couponCode.trim() !== '') {
    codes.push(LANDING_PROMOTION_CONFIG.couponCode.trim());
  }
  return Array.from(new Set(codes));
}

interface GeneratedLinkCardProps {
  title: string;
  description: string;
  url: string;
  isCopied: boolean;
  onCopy: () => void;
  emptyHint?: string;
}

// Card that displays a single generated link with a copy button, or a hint when the link cannot be built yet.
function GeneratedLinkCard({ title, description, url, isCopied, onCopy, emptyHint }: GeneratedLinkCardProps) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 mt-6">
      <div className="flex items-center mb-1">
        <FontAwesomeIcon icon={faLink} className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{description}</p>

      {url !== '' ? (
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg font-mono text-sm text-gray-800 dark:text-gray-200 break-all">
            {url}
          </div>
          <button
            type="button"
            onClick={onCopy}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors cursor-pointer font-medium whitespace-nowrap"
          >
            <FontAwesomeIcon icon={isCopied ? faCheck : faClipboard} className="w-4 h-4" />
            {isCopied ? 'Kopiert!' : 'Kopieren'}
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <FontAwesomeIcon icon={faInfoCircle} className="w-4 h-4" />
          {emptyHint ?? 'Bitte zuerst ein Paket auswählen.'}
        </div>
      )}
    </div>
  );
}

export default function AdminRegistrationUrlGenerator() {
  const { products, loading: productsLoading, getProduct, getTypeLabel, getPeriodLabel } = useSubscriptionProducts();
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionType | ''>('');
  const [selectedSecondPlan, setSelectedSecondPlan] = useState<SubscriptionType | ''>('');
  const [coupon, setCoupon] = useState('');
  const [referrerUser, setReferrerUser] = useState<User | null>(null);
  const [baseUrl, setBaseUrl] = useState<string>(() => getDefaultBaseUrl());
  const [copiedKey, setCopiedKey] = useState<'register' | 'checkout' | 'landing' | null>(null);

  const knownCoupons = getKnownCoupons();

  const selectedProduct = selectedPlan !== '' ? getProduct(selectedPlan) : undefined;
  const firstProductType = selectedProduct?.productType ?? null;
  // Ein Paket mit externer Danke-Seite kann nur als letztes Paket einer Kette stehen
  const firstHasExternalSuccess = hasExternalSuccessPage(selectedProduct);

  // Zweites Paket: nicht dasselbe und nicht dieselbe Produktfamilie (das würde ein Doppel-Abo ergeben)
  const isValidSecondPlan = (product: SubscriptionPriceProduct): boolean =>
    product.type !== selectedPlan && (product.productType === null || product.productType !== firstProductType);
  const secondPlanOptions = products.filter(isValidSecondPlan);

  const selectedPlans: SubscriptionType[] = [selectedPlan, selectedSecondPlan].filter((plan): plan is SubscriptionType => plan !== '');

  // Ohne hinterlegte Stripe-IDs kann für das Paket keine Checkout-Session erzeugt werden —
  // die generierten Links führen dann unweigerlich in einen Fehler.
  const missingStripeProducts = selectedPlans.filter(plan => !hasStripeCheckout(getProduct(plan)));
  const stripeUnreachableProducts = selectedPlans.flatMap(plan => {
    const product = getProduct(plan);
    return product !== undefined && hasStripeCheckout(product) && !product.stripeDataAvailable ? [product] : [];
  });

  const handleFirstPlanChange = (plan: SubscriptionType | '') => {
    setSelectedPlan(plan);
    const product = plan !== '' ? getProduct(plan) : undefined;
    const productType = product?.productType ?? null;
    // Zweites Paket verwerfen, wenn es zur neuen Auswahl nicht mehr passt
    if (hasExternalSuccessPage(product)) {
      setSelectedSecondPlan('');
      return;
    }
    if (selectedSecondPlan !== '') {
      const secondProduct = getProduct(selectedSecondPlan);
      const sameFamily = secondProduct?.productType !== null && secondProduct?.productType === productType;
      if (selectedSecondPlan === plan || sameFamily) {
        setSelectedSecondPlan('');
      }
    }
  };

  const trimmedBase = baseUrl.trim().replace(/\/$/, '');
  const trimmedCoupon = coupon.trim();
  const referrerCode = referrerUser !== null ? referrerUser.referrerCode.trim() : '';

  const planQuery = selectedPlans.map(plan => `plan=${plan}`).join('&');
  const couponQuery = trimmedCoupon !== '' ? `&coupon=${encodeURIComponent(trimmedCoupon)}` : '';
  const referrerQuery = referrerCode !== '' ? `&referrer=${encodeURIComponent(referrerCode)}` : '';
  const registrationUrl = selectedPlan !== ''
    ? `${trimmedBase}/auth/register?${planQuery}${couponQuery}${referrerQuery}`
    : '';
  const checkoutUrl = selectedPlan !== ''
    ? `${trimmedBase}/checkout?${planQuery}${couponQuery}${referrerQuery}`
    : '';
  // General landing link without a pre-selected plan; the user picks the plan themselves,
  // the referrer is appended to the registration URL on plan selection (see LandingPage).
  const landingUrl = referrerCode !== ''
    ? `${trimmedBase}/?referrer=${encodeURIComponent(referrerCode)}`
    : '';

  const handleCopy = (key: 'register' | 'checkout' | 'landing', url: string) => {
    if (url === '') {
      return;
    }
    void navigator.clipboard.writeText(url).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    });
  };

  const renderPlanOption = (product: SubscriptionPriceProduct) => (
    <option key={product.type} value={product.type}>
      {getTypeLabel(product.type)} · {getPeriodLabel(product.type)} — {product.type}
    </option>
  );

  return (
    <>
      <div className="flex items-center mb-8">
        <Link
          to="/admin"
          className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Registrierungs-Links
        </h1>
      </div>

      <div className="max-w-3xl">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Erstelle Links mit vorausgewähltem Paket und optional bereits aktivem Gutscheincode.
          Es werden drei Varianten generiert: ein <strong>Registrierungs-Link</strong> für neue Nutzer,
          ein <strong>Checkout-Link</strong>, der bestehende (eingeloggte) Nutzer direkt zum Checkout führt,
          und ein <strong>Landingpage-Link</strong> ohne vorausgewähltes Paket, bei dem der Nutzer selbst wählt
          und die Empfehlung durchgereicht wird.
          Der Coupon wird beim Checkout angewendet.
          Mit einem <strong>zusätzlichen Paket</strong> (z.B. Community + Ausbildung) läuft der Checkout in zwei
          Stripe-Schritten nacheinander; bereits vorhandene Pakete werden dabei übersprungen, der Coupon gilt für beide.
        </p>

        {/* Form card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 space-y-6">
          {/* Plan selection */}
          <div>
            <label htmlFor="plan-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Paket <span className="text-red-500">*</span>
            </label>
            <select
              id="plan-select"
              value={selectedPlan}
              onChange={(e) => handleFirstPlanChange(e.target.value as SubscriptionType | '')}
              disabled={productsLoading}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer disabled:cursor-wait"
            >
              <option value="">{productsLoading ? '-- Pakete werden geladen --' : '-- Paket wählen --'}</option>
              {products.map(renderPlanOption)}
            </select>
          </div>

          {/* Second plan selection (chained checkout) */}
          <div>
            <label htmlFor="second-plan-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Zusätzliches Paket <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <select
              id="second-plan-select"
              value={selectedSecondPlan}
              onChange={(e) => setSelectedSecondPlan(e.target.value as SubscriptionType | '')}
              disabled={productsLoading || selectedPlan === '' || firstHasExternalSuccess}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
            >
              <option value="">-- kein zweites Paket --</option>
              {secondPlanOptions.map(renderPlanOption)}
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {firstHasExternalSuccess
                ? 'Workshop-Pakete leiten nach dem Kauf auf eine externe Danke-Seite und können daher nicht mit einem zweiten Paket kombiniert werden.'
                : 'Wird nach dem ersten Paket in einem zweiten Stripe-Checkout gebucht. Pakete derselben Produktfamilie sind ausgeschlossen.'}
            </p>
          </div>

          {missingStripeProducts.length > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3 dark:border-red-700 dark:bg-red-900/20">
              <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 w-4 h-4 flex-shrink-0 text-red-600 dark:text-red-400" />
              <div className="text-sm">
                <p className="font-semibold text-red-900 dark:text-red-100">
                  Für {missingStripeProducts.length > 1 ? 'diese Pakete' : 'dieses Paket'} ist kein Stripe-Produkt hinterlegt:{' '}
                  <span className="font-mono">{missingStripeProducts.join(', ')}</span>
                </p>
                <p className="mt-1 text-red-800 dark:text-red-200">
                  Es kann nicht gekauft werden — der Checkout schlägt fehl, sobald jemand einen der
                  Links unten benutzt. Solche Pakete werden ausschließlich manuell vergeben.
                </p>
              </div>
            </div>
          )}

          {stripeUnreachableProducts.length > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-orange-300 bg-orange-50 p-3 dark:border-orange-700 dark:bg-orange-900/20">
              <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 w-4 h-4 flex-shrink-0 text-orange-600 dark:text-orange-400" />
              <div className="text-sm">
                <p className="font-semibold text-orange-900 dark:text-orange-100">
                  Stripe-Daten konnten nicht geladen werden
                </p>
                <p className="mt-1 text-orange-800 dark:text-orange-200">
                  {stripeUnreachableProducts.length > 1 ? 'Die Pakete haben' : 'Das Paket hat'} zwar Stripe-IDs hinterlegt, das Backend kommt aktuell aber nicht an{' '}
                  {stripeUnreachableProducts.map((product, index) => (
                    <span key={product.type}>
                      {index > 0 ? ', ' : ''}
                      <span className="font-mono">{product.stripeProductId}</span>
                    </span>
                  ))}. Der Checkout wird damit vermutlich ebenfalls fehlschlagen.
                </p>
              </div>
            </div>
          )}

          {/* Coupon input */}
          <div>
            <label htmlFor="coupon-input" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Gutscheincode <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <div className="relative">
              <FontAwesomeIcon
                icon={faTag}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              />
              <input
                id="coupon-input"
                type="text"
                value={coupon}
                onChange={(e) => setCoupon(e.target.value)}
                placeholder="z.B. PROMOX"
                className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Stripe-Coupon-ID oder Promotion-Code. Wird nicht vorab geprüft.
              {selectedPlans.length > 1 ? ' Bei zwei Paketen wird er auf beide Checkouts angewendet; passt er nur zu einem, läuft der andere ohne Rabatt.' : ''}
            </p>
            {knownCoupons.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Bekannte Codes:</span>
                {knownCoupons.map((code) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setCoupon(code)}
                    className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900 transition-colors cursor-pointer"
                  >
                    {code}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Referrer selection */}
          <div>
            <UserSelect
              id="referrer-select"
              label="Empfehlung durch (optional)"
              placeholder="Nutzer suchen..."
              selectedUser={referrerUser}
              onChange={setReferrerUser}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {referrerCode !== ''
                ? <>Empfehlungscode <span className="font-mono">{referrerCode}</span> wird an den Link angehängt (<span className="font-mono">&referrer={referrerCode}</span>).</>
                : 'Wähle optional einen Nutzer, der diese Registrierung empfiehlt.'}
            </p>
          </div>

          {/* Base URL */}
          <div>
            <label htmlFor="base-url-input" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Basis-URL
            </label>
            <input
              id="base-url-input"
              type="text"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://boersencampus.svenbolz.com"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Vorausgefüllt aus der Umgebung. Anpassbar, z.B. um aus Develop einen Produktions-Link zu erzeugen.
            </p>
          </div>
        </div>

        {/* Generated URL cards */}
        <GeneratedLinkCard
          title="Registrierungs-Link (neue Nutzer)"
          description="Führt neue Nutzer zur Registrierung; nach Abschluss startet automatisch der Checkout."
          url={registrationUrl}
          isCopied={copiedKey === 'register'}
          onCopy={() => handleCopy('register', registrationUrl)}
        />

        <GeneratedLinkCard
          title="Checkout-Link (bestehende Nutzer)"
          description="Führt eingeloggte Nutzer direkt zum Checkout. Ausgeloggte Nutzer werden zuvor zum Login geleitet."
          url={checkoutUrl}
          isCopied={copiedKey === 'checkout'}
          onCopy={() => handleCopy('checkout', checkoutUrl)}
        />

        <GeneratedLinkCard
          title="Landingpage-Link (Empfehlung)"
          description="Führt zur allgemeinen Landingpage ohne vorausgewähltes Paket; der Nutzer wählt es selbst. Der Empfehlungscode wird beim Paket-Klick automatisch an die Registrierung angehängt."
          url={landingUrl}
          isCopied={copiedKey === 'landing'}
          onCopy={() => handleCopy('landing', landingUrl)}
          emptyHint="Bitte zuerst einen Nutzer (Empfehlung) auswählen."
        />
      </div>
    </>
  );
}
